import React, { useState, useEffect } from 'react';
import './App.css';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';

function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentPage, setCurrentPage] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (hash === 'privacy') {
        setCurrentPage('privacy');
      } else if (hash === 'terms') {
        setCurrentPage('terms');
      } else {
        setCurrentPage('home');
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (currentPage === 'privacy') {
    return <PrivacyPolicy />;
  }

  if (currentPage === 'terms') {
    return <TermsOfService />;
  }

  return (
    <div className="App">
      {/* Navigation */}
      <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <div className="logo">
            <span className="logo-text">PlayTiny</span>
          </div>
          <ul className="nav-menu">
            <li><a href="#home">Home</a></li>
            <li><a href="#applications">Applications</a></li>
            <li><a href="#features">Features</a></li>
            <li><a href="#about">About</a></li>
            <li><a href="#contact">Contact</a></li>
          </ul>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-background">
          <div className="gradient-overlay"></div>
          <div className="animated-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
          </div>
        </div>
        <div className="hero-content">
          <h1 className="hero-title">
            <span className="title-line">Welcome to</span>
            <span className="title-main">PlayTiny</span>
          </h1>
          <p className="hero-subtitle">
            Crafting extraordinary mobile applications
            that bring value to millions of users worldwide
          </p>
          <div className="hero-buttons">
            <button className="btn btn-primary" onClick={() => scrollToSection('applications')}>Explore Applications</button>
            <button className="btn btn-secondary" onClick={() => scrollToSection('about')}>Learn More</button>
          </div>
        </div>
        <div className="scroll-indicator">
          <div className="mouse"></div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <h2 className="section-title">Why Choose PlayTiny?</h2>
          <p className="section-subtitle">We create immersive experiences that captivate and engage</p>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🚀</div>
              <h3>Innovative App Solutions</h3>
              <p>Cutting-edge features and engaging designs that keep users coming back for more.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📱</div>
              <h3>Mobile-First Design</h3>
              <p>Optimized for all mobile devices with smooth performance and intuitive controls.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🎨</div>
              <h3>Stunning Graphics</h3>
              <p>Beautiful visuals and polished UI that create memorable user experiences.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">⚡</div>
              <h3>Lightning Fast</h3>
              <p>Optimized performance ensuring a smooth experience even on older devices.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🌐</div>
              <h3>Global Reach</h3>
              <p>Our applications are enjoyed by users from all corners of the world.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">💎</div>
              <h3>Premium Quality</h3>
              <p>Every application is crafted with attention to detail and passion for excellence.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Applications Showcase */}
      <section id="applications" className="apps-showcase">
        <div className="container">
          <h2 className="section-title">Our Applications</h2>
          <p className="section-subtitle">Discover our collection of amazing mobile applications</p>
          <div className="apps-grid">
            <div className="app-card">
              <div className="app-image resto-chat"></div>
              <div className="app-info">
                <h3>Resto Chat</h3>
                <p>The ultimate social application for seamless communication</p>
                <div className="app-badges">
                  <span className="badge">Social</span>
                  <span className="badge">Communication</span>
                </div>
                <div className="app-actions">
                  <a
                    href="https://play.google.com/store/apps/details?id=com.restochat.playtiny&hl=en_IN"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="google-play-button"
                  >
                    <span className="play-icon">▶</span>
                    <div className="button-text">
                      <span className="get-it-on">GET IT ON</span>
                      <span className="google-play">Google Play</span>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            <div className="app-card coming-soon">
              <div className="app-image app-1">
                <div className="status-badge">Coming Soon</div>
              </div>
              <div className="app-info">
                <h3>Pro Tasker</h3>
                <p>Advanced productivity tool for modern professionals</p>
                <div className="app-badges">
                  <span className="badge">Productivity</span>
                  <span className="badge">Business</span>
                </div>
              </div>
            </div>

            <div className="app-card coming-soon">
              <div className="app-image app-2">
                <div className="status-badge">Coming Soon</div>
              </div>
              <div className="app-info">
                <h3>Pulse Health</h3>
                <p>Integrated wellness and fitness tracking system</p>
                <div className="app-badges">
                  <span className="badge">Health</span>
                  <span className="badge">Fitness</span>
                </div>
              </div>
            </div>

            <div className="app-card coming-soon">
              <div className="app-image app-3">
                <div className="status-badge">Coming Soon</div>
              </div>
              <div className="app-info">
                <h3>Cloud Sync</h3>
                <p>Seamless file management and cloud storage solution</p>
                <div className="app-badges">
                  <span className="badge">Utility</span>
                  <span className="badge">Cloud</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2 className="section-title">About PlayTiny</h2>
              <p>
                PlayTiny is a leading mobile application development company dedicated to creating
                exceptional digital experiences. Our team of passionate developers, designers,
                and application specialists work tirelessly to bring innovative and high-quality
                applications to mobile platforms.
              </p>
              <p>
                We believe that great applications should be accessible to everyone, which is why
                we focus on creating applications that are not only functional but also optimized for
                performance across all devices. From utility tools to social platforms,
                we're committed to delivering quality digital solutions.
              </p>
              <div className="stats">
                <div className="stat-item">
                  <div className="stat-number">5+</div>
                  <div className="stat-label">Applications Published</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">1M+</div>
                  <div className="stat-label">Active Users</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">5+</div>
                  <div className="stat-label">Team Members</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="container">
          <h2 className="section-title">Get In Touch</h2>
          <p className="section-subtitle">Have a question or want to collaborate? We'd love to hear from you!</p>
          <div className="contact-content">
            <div className="contact-info">
              <div className="contact-item">
                <div className="contact-icon">📧</div>
                <div>
                  <h4>Email</h4>
                  <p>restofthings8@gmail.com</p>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">📱</div>
                <div>
                  <h4>Phone</h4>
                  <p>+91 97054xxxxxx</p>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">📍</div>
                <div>
                  <h4>Location</h4>
                  <p>Hayathnagar, Telangana, India</p>
                </div>
              </div>
            </div>
            <form className="contact-form">
              <input type="text" placeholder="Your Name" required />
              <input type="email" placeholder="Your Email" required />
              <textarea placeholder="Your Message" rows="5" required></textarea>
              <button type="submit" className="btn btn-primary">Send Message</button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>PlayTiny</h3>
              <p>Creating amazing mobile application experiences since 2020.</p>
              <div className="social-links">
                <a href="#" aria-label="Facebook">📘</a>
                <a href="#" aria-label="Twitter">🐦</a>
                <a href="#" aria-label="Instagram">📷</a>
                <a href="#" aria-label="LinkedIn">💼</a>
              </div>
            </div>
            <div className="footer-section">
              <h4>Quick Links</h4>
              <ul>
                <li><a href="#home">Home</a></li>
                <li><a href="#applications">Applications</a></li>
                <li><a href="#features">Features</a></li>
                <li><a href="#about">About</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Legal</h4>
              <ul>
                <li><a href="#privacy">Privacy Policy</a></li>
                <li><a href="#terms">Terms of Service</a></li>
                <li><a href="#privacy">Cookie Policy</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Newsletter</h4>
              <p>Subscribe to get updates on new applications and features.</p>
              <form className="newsletter-form">
                <input type="email" placeholder="Enter your email" />
                <button type="submit" className="btn btn-primary">Subscribe</button>
              </form>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 PlayTiny. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
